import { useState } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import {
  useMfaPasskeyAuthOptionsMutation,
  useMfaPasskeyAuthVerifyMutation,
  useMfaPasskeyRegisterOptionsMutation,
  useMfaPasskeyRegisterVerifyMutation,
  type MfaVerifyResponse,
} from '@/shared/api/auth';
import { isMfaSessionExpiredError, passkeyErrorMessage } from '@/shared/utils';

interface Params {
  mfaPending: string;
  mode: 'enroll' | 'verify';
  /** Receives the raw dual-outcome result; the container adopts a session or captures a proof. */
  onSuccess: (view: MfaVerifyResponse) => void;
  onExpired: () => void;
}

/**
 * Drives the passkey ceremony for both enrollment (register → register/verify) and verification
 * (auth/options → auth/verify), each atomic from the user's view — the credential is registered or
 * the assertion verified in a single `run()`. State lives here so callers can fire the ceremony from
 * any surface (a method-selector click, a retry button) and render a spinner / error where they like.
 */
export const usePasskeyCeremony = ({ mfaPending, mode, onSuccess, onExpired }: Params) => {
  const isEnroll = mode === 'enroll';

  const { passkeyRegisterOptions } = useMfaPasskeyRegisterOptionsMutation();
  const { passkeyRegisterVerify } = useMfaPasskeyRegisterVerifyMutation();
  const { passkeyAuthOptions } = useMfaPasskeyAuthOptionsMutation();
  const { passkeyAuthVerify } = useMfaPasskeyAuthVerifyMutation();

  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    setIsRunning(true);

    try {
      if (isEnroll) {
        const { optionsJSON } = await passkeyRegisterOptions({ mfaPending });
        const response = await startRegistration({ optionsJSON: JSON.parse(optionsJSON) });
        const view = await passkeyRegisterVerify({ mfaPending, response: JSON.stringify(response) });
        onSuccess(view);
      } else {
        const { optionsJSON } = await passkeyAuthOptions({ mfaPending });
        const response = await startAuthentication({ optionsJSON: JSON.parse(optionsJSON) });
        const view = await passkeyAuthVerify({ mfaPending, response: JSON.stringify(response) });
        onSuccess(view);
      }
    } catch (caught) {
      if (isMfaSessionExpiredError(caught)) {
        onExpired();
      } else {
        setError(passkeyErrorMessage(caught));
      }
    } finally {
      setIsRunning(false);
    }
  };

  /** Clear a failed ceremony so the caller can return to its pre-ceremony surface. */
  const reset = () => setError(null);

  return { run, isRunning, error, reset };
};
